import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { TreeNode } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';
import { Subject, takeUntil } from 'rxjs'
import { PerformanceParams } from '@core/models/interfaces/report';
import { UserDetail } from '@core/models/interfaces/user';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomCalendarComponent } from '@shared/components/custom-calendar/custom-calendar.component';
import { FormControlComponent } from '@shared/components/form-control/form-control.component';
import { UserFormDropdownComponent } from '@shared/components/dropdown/user-form-dropdown/user-form-dropdown.component';
import { ReportField, requiredExportFieldsPerformanceKey } from '@core/models/interfaces/report-field';
import { ReportService } from '@core/services/report.service';
import { ReportFieldSelectionComponent } from '@shared/components/report-field-selection/report-field-selection.component';
@Component({
  selector: 'app-performance-selection',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MultiSelectModule,
    CalendarModule,
    TranslateModule,
    DropdownModule,
    TreeSelectModule,
    FormControlComponent,
    ButtonComponent,
    CustomCalendarComponent,
    UserFormDropdownComponent,
    ReportFieldSelectionComponent
  ],
  templateUrl: './performance-selection.component.html',
  styleUrl: './performance-selection.component.scss'
})
export class PerformanceSelectionComponent implements OnInit, OnChanges {
  @Input({ required: true }) title = '';
  @Input({ required: true }) isPerformance = true;
  @Input() isLoading: boolean = false;
  @Input() params: PerformanceParams;
  @Input() isRunning: boolean = false;

  @Input() userGroups: TreeNode[] = [];
  @Output() paramsChange = new EventEmitter<PerformanceParams>();
  @Output() isRunningChange = new EventEmitter<boolean>();

  readonly translatePrefix = 'report.';
  readonly dateNow: Date = new Date();

  performanceForm = this.fb.group({
    users: this.fb.control<UserDetail[]>([]),
    userGroups: this.fb.control<TreeNode[]>([{ label: '' }])
  });

  dates: Array<Date | undefined> = [];

  private unsubscribe$ = new Subject<void>();
  exportSelectedFields: ReportField[] = []
  isSelectionDialogVisible: boolean = false;
  isAllExportFieldsSelected: boolean = true;

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.fetchExportSelectedFields();
    // Nếu đã có params (VD: load lại từ URL) thì đồng bộ form ngay lần khởi tạo.
    if (this.params) {
      this.syncFormFromParams(this.params);
    }

    this.performanceForm.controls.users.valueChanges.subscribe(usersValue => {
      const userIds = usersValue?.map(item => item.id);
      this.checkDisableUserGroupsControl(userIds);
    });

    this.performanceForm.controls.userGroups.valueChanges.subscribe(userGroupsValue => {
      this.checkDisableUsersControl(userGroupsValue);
    });
  }

  /**
   * Lắng nghe thay đổi @Input params (khi ReportComponent map từ queryParams).
   * Đảm bảo multi-select và date hiển thị đúng theo URL.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['params']?.currentValue) {
      this.syncFormFromParams(changes['params'].currentValue as PerformanceParams);
    }
  }

  /**
   * Đồng bộ form, dates và exportSelectedFields từ PerformanceParams.
   * Giống cách fix tab Production: sync exportSelectedFields khi back về từ page khác.
   */
  private syncFormFromParams(params: PerformanceParams) {
    // Trim fullName để tránh khoảng trắng thừa (giống fix tab Production).
    const sanitizedUsers = params.users?.map(u => ({
      ...u,
      fullName: u.fullName?.trim() || ''
    })) || [];

    this.performanceForm.patchValue({
      users: sanitizedUsers.length ? sanitizedUsers : [],
      userGroups: params.groups?.length ? params.groups : [{ label: '' }]
    });

    this.dates = [
      params.createdFrom ? new Date(params.createdFrom) : undefined,
      params.createdTo ? new Date(params.createdTo) : undefined
    ].filter(item => !!item);

    this.checkDisableUsersControl(params.groups);

    const userIds = params.users?.map(item => item.id);
    this.checkDisableUserGroupsControl(userIds);

    // Đồng bộ exportSelectedFields từ params.visibleFields (khi back về từ page khác).
    if (params.visibleFields?.length && this.exportSelectedFields?.length) {
      const selectedFromParams = params.visibleFields;
      this.exportSelectedFields.forEach(e => {
        e.isSelected = !!selectedFromParams?.find(f => f === e.key);
      });
      this.onExportFieldChanged();
    }
  }

  get usersValue() {
    return this.performanceForm.value.users?.filter(item => !!item);
  }

  get userGroupsValue() {
    return this.performanceForm.value.userGroups?.filter(item => !!item.key);
  }

  checkDisableUserGroupsControl(userIds?: string[] | null) {
    if (userIds?.filter(item => !!item)?.length) {
      this.performanceForm.controls.userGroups.disable({ emitEvent: false });
    } else {
      this.performanceForm.controls.userGroups.enable({ emitEvent: false });
    }
  }

  checkDisableUsersControl(userGroups?: TreeNode[] | null) {
    if (userGroups?.filter(item => !!item.key).length) {
      this.performanceForm.controls.users.disable({ emitEvent: false });
    } else {
      this.performanceForm.controls.users.enable({ emitEvent: false });
    }
  }

  onRunReport() {
    const value = this.performanceForm.value;
    const createdFrom = this.dates?.[0] ? moment(this.dates?.[0]).toDate().toISOString() : undefined;
    const createdTo = this.dates?.[1] ? moment(this.dates?.[1]).toDate().toISOString() : undefined;

    this.params = {
      userIds: value.users?.map(item => item.id) || [],
      users: value.users || [],
      groups: value.userGroups || [],
      createdFrom,
      createdTo,
      visibleFields: this.exportSelectedFieldsList(),
    };
    this.paramsChange.emit(this.params);

    this.isRunning = true;
    this.isRunningChange.emit(this.isRunning);
  }

  getTreeNodeValue(value: TreeNode[]) {
    return value
      ?.filter(item => !!item.label)
      ?.map(item => item.label)
      .join(', ');
  }

  onReset() {
    this.performanceForm.patchValue({
      users: [],
      userGroups: [{ label: '' }]
    });
    this.dates = [];
    this.isAllExportFieldsSelected = true;
    this.exportSelectedFields.forEach(e => e.isSelected = true)
    this.onExportFieldChanged()
  }

  fetchExportSelectedFields() {
    if (this.isPerformance) {
      return this.reportService
        .getPerformanceReportFields()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (result) => {
            this.exportSelectedFields = result.data as ReportField[]
            if (this.params) {
              const selectedFromParams = this.params.visibleFields;
              this.exportSelectedFields.forEach(e => {
                if (selectedFromParams?.find(f => f === e.key)) {
                  e.isSelected = true;
                } else {
                  e.isSelected = false
                }
              })
            } else {
              this.exportSelectedFields.forEach(e => {
                e.isSelected = true;
              })
            }
            this.onExportFieldChanged()
          },
          error: () => { }
        })
    } else {
      return this.reportService
      .getLeadReportFields()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          this.exportSelectedFields = result.data as ReportField[]
          if (this.params) {
            const selectedFromParams = this.params.visibleFields;
            this.exportSelectedFields.forEach(e => {
              if (selectedFromParams?.find(f => f === e.key)) {
                e.isSelected = true;
              } else {
                e.isSelected = false
              }
            })
          } else {
            this.exportSelectedFields.forEach(e => {
              e.isSelected = true;
            })
          }
          this.onExportFieldChanged()
        },
        error: () => { }
      })
    }
    
  }

  toggleAllExportFields(isSelected: boolean) {
    this.isAllExportFieldsSelected = isSelected;
    if (this.isAllExportFieldsSelected) {
      this.exportSelectedFields.forEach(e => e.isSelected = true)
    } else {
      this.exportSelectedFields.forEach(e => {
        if (requiredExportFieldsPerformanceKey.includes(e.key)) {
          e.isSelected = true;
        } else {
          e.isSelected = false
        }
      })
    }
  }

  onExportFieldChanged() {
    this.isAllExportFieldsSelected =
      this.exportSelectedFields.length > 0 &&
      this.exportSelectedFields.every(e => e.isSelected === true)
    this.cdr.markForCheck()
  }

  get requiredExportFieldsPerformanceKey() {
    return requiredExportFieldsPerformanceKey
  }

  get exportSelectedFieldsText(): string {
    return `Select fields (${this.isAllExportFieldsSelected ? 'All' : this.exportSelectedFieldsList().length})`
  }

  exportSelectedFieldsList() {
    return this.exportSelectedFields.filter(e => e.isSelected === true).map(e => e.key)
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
